import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

export interface DialogInput {
  type: 'select' | 'number' | 'text';
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  min?: number;
  options?: { value: string; label: string }[];
}

export interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  /** Quando presente, o modal vira um "prompt": resolve com o valor digitado/selecionado. */
  input?: DialogInput;
}

type ShowDialogFn = (options: ConfirmOptions) => Promise<boolean | string | null>;

const ConfirmContext = createContext<ShowDialogFn | null>(null);

/** Confirmação sim/não: `const ok = await confirm({ message })`. */
export const useConfirm = (): ((options: ConfirmOptions) => Promise<boolean>) => {
  const show = useDialog();
  return useCallback((options) => show(options) as Promise<boolean>, [show]);
};

/** Prompt com campo (select/número/texto): resolve com o valor ou null se cancelado. */
export const usePrompt = (): ((options: ConfirmOptions) => Promise<string | null>) => {
  const show = useDialog();
  return useCallback((options) => show(options) as Promise<string | null>, [show]);
};

const useDialog = (): ShowDialogFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error('useConfirm/usePrompt devem ser usados dentro de <ConfirmProvider>');
  }
  return ctx;
};

/**
 * Provider que renderiza um único modal e expõe confirm()/prompt().
 * Substitui os window.confirm() e window.prompt() nativos.
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [value, setValue] = useState('');
  const resolver = useRef<((value: boolean | string | null) => void) | null>(null);

  const showDialog = useCallback<ShowDialogFn>((opts) => {
    setOptions(opts);
    setValue(opts.input?.defaultValue ?? (opts.input?.type === 'select' ? opts.input.options?.[0]?.value ?? '' : ''));
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = (confirmed: boolean) => {
    if (resolver.current) {
      if (options?.input) {
        resolver.current(confirmed ? value : null);
      } else {
        resolver.current(confirmed);
      }
      resolver.current = null;
    }
    setOptions(null);
  };

  const isDanger = options?.variant !== 'primary';
  const canConfirm = !options?.input?.required || value !== '';

  // Fecha com ESC
  useEffect(() => {
    if (!options) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, value]);

  return (
    <ConfirmContext.Provider value={showDialog}>
      {children}
      {options && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => close(false)} aria-hidden="true" />
          <div role="dialog" aria-modal="true" className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDanger ? 'bg-red-100' : 'bg-blue-100'}`}>
                <AlertTriangle className={`w-5 h-5 ${isDanger ? 'text-red-600' : 'text-blue-600'}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{options.title || 'Confirmar ação'}</h3>
                {options.message && <p className="text-sm text-gray-600 mt-1">{options.message}</p>}
              </div>
            </div>

            {options.input && (
              <div className="mt-4">
                {options.input.label && (
                  <label className="block text-sm font-medium text-gray-700 mb-1">{options.input.label}</label>
                )}
                {options.input.type === 'select' ? (
                  <select
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {options.input.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={options.input.type}
                    value={value}
                    min={options.input.min}
                    placeholder={options.input.placeholder}
                    onChange={(e) => setValue(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    autoFocus
                  />
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => close(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {options.cancelLabel || 'Cancelar'}
              </button>
              <button
                type="button"
                disabled={!canConfirm}
                onClick={() => close(true)}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${isDanger ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'}`}
              >
                {options.confirmLabel || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export default ConfirmProvider;
